import React, { useId } from 'react';

type Props = { value: string; onChange: (value: string) => void; error?: string | null };
const controlClass = 'min-h-11 w-full rounded-control border border-muted-foreground! bg-background px-3 py-2 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

/** Presentational editor; the host owns defaults, validation and persistence. */
export function BroadbandFactsEditor({ value, onChange, error }: Props) {
  const id = useId();
  let config: Record<string, unknown>;
  try {
    config = JSON.parse(value);
    if (!config || typeof config !== 'object' || Array.isArray(config)) throw new Error();
  } catch {
    return <p role="alert" className="text-sm font-medium text-foreground">The Broadband Facts configuration is invalid. Remove this attribute and configure it again.</p>;
  }
  const patch = (changes: Record<string, unknown>) => onChange(JSON.stringify({ ...config, ...changes }));
  const nested = (key: string) => (config[key] ?? {}) as Record<string, unknown>;
  const patchNested = (key: string, changes: Record<string, unknown>) => patch({ [key]: { ...nested(key), ...changes } });
  const field = (key: string, label: string, type = 'text', help?: string) => (
    <label key={key} className="flex min-w-0 flex-col gap-2 text-sm font-medium text-foreground">
      {label}
      <input aria-label={label} className={controlClass} type={type} min={type === 'number' ? 0 : undefined} step={type === 'number' ? 'any' : undefined}
        value={String(config[key] ?? '')} onChange={event => patch({ [key]: type === 'number' ? (event.target.value === '' ? undefined : Number(event.target.value)) : event.target.value })} />
      {help ? <span className="text-xs font-normal text-muted-foreground">{help}</span> : null}
    </label>
  );
  return (
    <section aria-labelledby={`${id}-title`} className="w-full min-w-0 space-y-5">
      <div>
        <h3 id={`${id}-title`} className="font-semibold text-foreground">Broadband Facts</h3>
        <p className="mt-1 text-sm text-muted-foreground">Drafts stay in the catalog editor. Only complete published facts appear with internet plans.</p>
      </div>
      {error ? <p role="alert" className="text-sm font-medium text-foreground">{error}</p> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm font-medium text-foreground">Status
          <select aria-label="Status" className={controlClass} value={String(config.status)} onChange={event => patch({ status: event.target.value })}>
            <option value="draft">Draft</option><option value="published">Published</option>
          </select>
        </label>
        <label className="space-y-2 text-sm font-medium text-foreground">Service type
          <select aria-label="Service type" className={controlClass} value={String(config.service_type)} onChange={event => patch({ service_type: event.target.value })}>
            <option value="fixed">Fixed broadband</option><option value="mobile">Mobile broadband</option>
          </select>
        </label>
        {field('provider_name', 'Provider name')}
        {field('service_plan_name', 'Plan name')}
        {field('fcc_registration_number', 'FCC registration number (FRN)', 'text', 'Enter the provider’s real ten-digit FRN. The plan identifier is generated for each offer and pricebook.')}
        {field('contract_terms_url', 'Contract terms URL', 'url', 'Contract duration comes from the offer’s term.')}
      </div>
      <fieldset className="space-y-4 rounded-control border border-border p-4">
        <legend className="px-1 text-sm font-semibold text-foreground">Typical performance</legend>
        <label className="flex min-h-11 items-center gap-3 text-sm text-foreground">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={config.performance_is_placeholder === true} onChange={event => patch({ performance_is_placeholder: event.target.checked })} />
          Performance values are placeholders
        </label>
        {config.performance_is_placeholder ? <p role="status" className="text-sm text-muted-foreground">Example values, not measured performance. Replace them and clear this flag before publishing.</p> : null}
        <div className="grid gap-4 sm:grid-cols-3">
          {field('typical_download_speed_mbps', 'Typical download (Mbps)', 'number')}
          {field('typical_upload_speed_mbps', 'Typical upload (Mbps)', 'number')}
          {field('typical_latency_ms', 'Typical latency (ms)', 'number')}
        </div>
      </fieldset>
      <section className="rounded-control border border-border bg-muted/20 p-4" aria-label="Calculated label pricing">
        <h4 className="text-sm font-semibold text-foreground">Prices, fees and plan identifier are calculated</h4>
        <p className="mt-2 text-sm text-muted-foreground">Commerce uses the selected offer and pricebook, including required variant fees, percentage fees and installation charges. Edit those amounts in Fees and Pricebooks. Optional fees and checkout discounts are excluded from the label. Labels currently support USD monthly offers.</p>
      </section>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm font-medium text-foreground">Government taxes
          <select aria-label="Government taxes" className={controlClass} value={String(nested('government_taxes').treatment ?? '')} onChange={event => patchNested('government_taxes', { treatment: event.target.value })}>
            <option value="">Not configured</option><option value="included">Included</option><option value="varies_by_location">Varies by location</option>
          </select>
        </label>
        <label className="space-y-2 text-sm font-medium text-foreground">Data allowance
          <select aria-label="Data allowance" className={controlClass} value={nested('data_allowance').unlimited === true ? 'unlimited' : nested('data_allowance').unlimited === false ? 'limited' : ''} onChange={event => patch({ data_allowance: event.target.value === '' ? undefined : { unlimited: event.target.value === 'unlimited' } })}>
            <option value="">Not configured</option><option value="unlimited">Unlimited</option><option value="limited">Limited</option>
          </select>
        </label>
        {nested('data_allowance').unlimited === false ? <>
          <label className="space-y-2 text-sm font-medium text-foreground">Included data
            <input className={controlClass} type="number" min="0" value={String(nested('data_allowance').amount ?? '')} onChange={event => patchNested('data_allowance', { amount: event.target.value === '' ? undefined : Number(event.target.value), unit: nested('data_allowance').unit || 'GB' })} />
          </label>
          <label className="space-y-2 text-sm font-medium text-foreground">Data unit
            <select aria-label="Data unit" className={controlClass} value={String(nested('data_allowance').unit || 'GB')} onChange={event => patchNested('data_allowance', { unit: event.target.value })}>
              <option value="MB">MB</option><option value="GB">GB</option><option value="TB">TB</option>
            </select>
          </label>
          <label className="space-y-2 text-sm font-medium text-foreground">Overage or speed-reduction terms
            <textarea className={controlClass} value={String(nested('data_allowance').reduced_service_description ?? '')} onChange={event => patchNested('data_allowance', { reduced_service_description: event.target.value })} />
          </label>
        </> : null}
        {field('discounts_and_bundles_url', 'Discounts and bundles URL', 'url')}
        {field('network_management_policy_url', 'Network management policy URL', 'url')}
        {field('privacy_policy_url', 'Privacy policy URL', 'url')}
        {field('customer_support_url', 'Customer support URL', 'url')}
        {field('customer_support_phone', 'Customer support phone', 'tel')}
      </div>
    </section>
  );
}
